import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { API } from '../../config';
import AdminDashboardStats from './AdminDashboardStats';
import AdminHeroImages from './AdminHeroImages';
import AdminHeader from './AdminHeader';
import AdminFooter from './AdminFooter';
import AdminStats from './AdminStats';
import AdminServices from './AdminServices';
import AdminFeatures from './AdminFeatures';
import AdminSiteContent from './AdminSiteContent';
import {
    LayoutGrid,
    Image as ImageIcon,
    LayoutTemplate,
    Ticket,
    Settings,
    Menu,
    X,
    FileText,
    LogOut,
    BarChart3,
    Layers,
    Star,
    BookOpen,
    LayoutDashboard,
    Home
} from 'lucide-react';

import { useAppContext } from '../../context/AppContext';
import logo from '../../images/FINAL-DESIGN-LOGO-01.png';

const AdminDashboard = () => {
    const { user, logout } = useAppContext();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('dashboard');
    const [stats, setStats] = useState({});
    const [heroImages, setHeroImages] = useState([]);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    useEffect(() => {
        fetchHeroImagesPreview();
        fetchDashboardStats();
    }, []);

    // Close sidebar when tab changes on mobile
    useEffect(() => {
        if (window.innerWidth < 1024) {
            setIsSidebarOpen(false);
        }
    }, [activeTab]);

    const fetchHeroImagesPreview = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API}/hero-images`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setHeroImages(response.data || []);
        } catch (error) {
            // silent fail for preview
        }
    };

    const fetchDashboardStats = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API}/dashboard/stats`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setStats(response.data || {});
        } catch (error) {
            setStats({});
            toast.error('Failed to fetch dashboard stats');
        }
    };

    if (user?.role !== 'admin') {
        return (
            <div className="min-h-screen bg-gray-50 pt-20 pb-12 flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-800 mb-4">Access Denied</h2>
                    <p className="text-gray-600">You need admin privileges to access this page.</p>
                </div>
            </div>
        );
    }

    const tabs = [
        { id: 'dashboard', name: 'Overview', icon: <LayoutDashboard className="h-5 w-5" /> },
        { id: 'heroImages', name: 'Hero Images', icon: <ImageIcon className="h-5 w-5" /> },
        { id: 'stats', name: 'Homepage Stats', icon: <BarChart3 className="h-5 w-5" /> },
        { id: 'services', name: 'Services', icon: <Layers className="h-5 w-5" /> },
        { id: 'features', name: 'Choice Features', icon: <Star className="h-5 w-5" /> },
        { id: 'siteContent', name: 'Site Content', icon: <FileText className="h-5 w-5" /> },
        { id: 'footer', name: 'Footer Settings', icon: <Settings className="h-5 w-5" /> },
    ];

    return (
        <div className="flex min-h-screen bg-[#F3F4F6]">
            {/* Mobile Sidebar Overlay */}
            {isSidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setIsSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside className={`
                fixed lg:sticky lg:top-0 inset-y-0 left-0 z-50
                w-64 bg-agro-green-dark text-gray-300 flex flex-col h-screen lg:min-h-screen
                transform transition-transform duration-300 ease-in-out
                ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
            `}>
                <div className="h-24 flex items-center justify-between px-6 border-b border-white/5">
                    <img src={logo} alt="SR Agro Farms" className="h-10 w-auto brightness-0 invert" />
                    <button
                        onClick={() => setIsSidebarOpen(false)}
                        className="lg:hidden text-gray-400 hover:text-white"
                    >
                        <X className="h-6 w-6" />
                    </button>
                </div>

                <nav className="flex-1 px-4 py-6 space-y-1.5 overflow-y-auto custom-scrollbar">
                    <p className="px-4 text-[10px] font-bold uppercase tracking-[0.2em] text-agro-lime/30 mb-4">Main Menu</p>
                    {tabs.map((tab, index) => (
                        <motion.button
                            key={tab.id}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.03 }}
                            onClick={() => setActiveTab(tab.id)}
                            className={`w-full flex items-center px-4 py-3 rounded-2xl transition-all duration-300 group ${activeTab === tab.id
                                ? 'bg-agro-lime text-agro-green-dark shadow-xl shadow-agro-lime/5'
                                : 'hover:bg-white/10 hover:text-white text-white'
                                }`}
                        >
                            <span className={`mr-3 transition-colors ${activeTab === tab.id ? 'text-agro-green-dark' : 'text-white/60 group-hover:text-agro-lime'}`}>
                                {tab.icon}
                            </span>
                            <span className="font-bold text-sm font-sans">{tab.name}</span>
                        </motion.button>
                    ))}

                    <div className="pt-6 mt-6 border-t border-white/5">
                        <p className="px-4 text-[10px] font-bold uppercase tracking-[0.2em] text-agro-lime/30 mb-4">External</p>
                        <a href="/" className="w-full flex items-center px-4 py-3 text-white hover:text-white hover:bg-white/10 rounded-2xl transition-all group">
                            <span className="mr-3 text-white/60 group-hover:text-agro-lime transition-colors"><Home className="h-5 w-5" /></span>
                            <span className="font-bold text-sm font-sans">View Website</span>
                        </a>
                    </div>
                </nav>

                <div className="p-4 border-t border-white/5 pb-24 lg:pb-4">
                    <button
                        onClick={() => {
                            logout();
                            navigate('/');
                        }}
                        className="w-full flex items-center px-4 py-3 text-white/40 hover:text-red-400 hover:bg-red-400/5 rounded-2xl transition-all group"
                    >
                        <LogOut className="h-5 w-5 mr-3 text-white/20 group-hover:text-red-400 transition-colors" />
                        <span className="font-bold text-sm font-sans">Logout</span>
                    </button>
                </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 flex flex-col min-h-screen w-full lg:w-auto">
                {/* Mobile Header */}
                <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between sticky top-0 z-30">
                    <button
                        onClick={() => setIsSidebarOpen(true)}
                        className="p-2 -ml-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                    >
                        <Menu className="h-6 w-6" />
                    </button>
                    <span className="font-bold text-gray-900">Admin Panel</span>
                    <div className="w-10" /> {/* Spacer for centering */}
                </div>

                <div className="hidden lg:block">
                    <AdminHeader user={user} />
                </div>

                <div className="flex-1 p-4 lg:p-8 overflow-y-auto overflow-x-hidden">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={activeTab}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                        >
                            {activeTab === 'dashboard' && <AdminDashboardStats stats={stats} setActiveTab={setActiveTab} />}
                            {activeTab === 'heroImages' && <AdminHeroImages />}
                            {activeTab === 'stats' && <AdminStats />}
                            {activeTab === 'services' && <AdminServices />}
                            {activeTab === 'features' && <AdminFeatures />}
                            {activeTab === 'siteContent' && <AdminSiteContent />}
                            {activeTab === 'footer' && <AdminFooter />}
                        </motion.div>
                    </AnimatePresence>
                </div>
            </main>
        </div>
    );
};

export default AdminDashboard;
